import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslations } from '../../i18n';
import { UserPlus, Trash2, Loader2, ChevronUp, ChevronDown, Rocket } from 'lucide-react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { Link } from 'react-router-dom';

interface CarAdmin {
    car_id: string;
    email: string;
    created_at: string;
}

interface CarAdminsSectionProps {
    carId: string;
    isOwner: boolean;
    onRemovingAccess?: () => void;
    isAdminView?: boolean;
    isPro?: boolean;
}

import { usePreferences } from '../../contexts/PreferencesContext';

export function CarAdminsSection({ carId, isOwner, onRemovingAccess, isAdminView, isPro: propsIsPro }: CarAdminsSectionProps) {
    const { t } = useTranslations();
    const { preferences } = usePreferences();
    const isPro = typeof propsIsPro === 'boolean' ? propsIsPro : (preferences?.plan_type && preferences.plan_type !== 'free') || isAdminView;
    const [admins, setAdmins] = useState<CarAdmin[]>([]);
    const [loading, setLoading] = useState(true);
    const [newEmail, setNewEmail] = useState('');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [adminToRemove, setAdminToRemove] = useState<string | null>(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    const [showRemoveSelfConfirm, setShowRemoveSelfConfirm] = useState(false);
    const [removingSelf, setRemovingSelf] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (isOwner) {
            loadAdmins();
        } else {
            setLoading(false);
        }
    }, [carId, isOwner]);

    const loadAdmins = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('car_admins')
                .select('*')
                .eq('car_id', carId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAdmins(data || []);
        } catch (err) {
            console.error('Error loading car admins:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAdmin = async () => {
        const emailToAdd = newEmail.trim();
        if (!emailToAdd || !emailToAdd.includes('@')) return;

        if (!isPro && !isAdminView) {
            setError(t.common.proOnly || 'Pro only');
            return;
        }

        setAdding(true);
        setError(null);
        try {
            const { error } = await supabase
                .from('car_admins')
                .insert({ car_id: carId, email: emailToAdd.toLowerCase() });

            if (error) {
                if (error.code === '23505') {
                    setError('This email is already an administrator.');
                } else {
                    throw error;
                }
            } else {
                setNewEmail('');
                await loadAdmins();
            }
        } catch (err: any) {
            console.error('Error adding car admin:', err);
            setError(err.message || 'Failed to add administrator');
        } finally {
            setAdding(false);
        }
    };

    const confirmRemoveAdmin = (email: string) => {
        setAdminToRemove(email);
        setShowRemoveConfirm(true);
    };

    const handleRemoveAdmin = async () => {
        if (!adminToRemove) return;
        try {
            const { error } = await supabase
                .from('car_admins')
                .delete()
                .eq('car_id', carId)
                .eq('email', adminToRemove);

            if (error) throw error;
            await loadAdmins();
        } catch (err) {
            console.error('Error removing car admin:', err);
        } finally {
            setShowRemoveConfirm(false);
            setAdminToRemove(null);
        }
    };

    const confirmRemoveSelf = () => {
        setShowRemoveSelfConfirm(true);
    };

    const handleRemoveSelf = async () => {
        setRemovingSelf(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user || !user.email) throw new Error('No user email');

            const { error } = await supabase
                .from('car_admins')
                .delete()
                .eq('car_id', carId)
                .eq('email', user.email);

            if (error) throw error;

            if (onRemovingAccess) {
                onRemovingAccess();
            }
        } catch (err) {
            console.error('Error removing self from car admins:', err);
        } finally {
            setRemovingSelf(false);
            setShowRemoveSelfConfirm(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!isOwner) {
        return (
            <div className="pt-4 px-2">
                <div className="bg-white dark:bg-slate-900/40 border border-red-100 dark:border-red-900/20 rounded-2xl overflow-hidden shadow-sm">
                    <button
                        type="button"
                        onClick={confirmRemoveSelf}
                        disabled={removingSelf}
                        className="w-full text-left p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center justify-between disabled:opacity-50"
                    >
                        {removingSelf ? <Loader2 className="w-5 h-5 animate-spin" /> : (t.cars as any).removeMyAccess}
                        <Trash2 size={18} />
                    </button>
                </div>
                <ConfirmModal
                    isOpen={showRemoveSelfConfirm}
                    title={(t.cars as any).removeMyAccess}
                    message={(t.cars as any).removeMyAccessConfirm}
                    confirmLabel={t.common.delete || 'Remove'}
                    cancelLabel={t.common.cancel}
                    onConfirm={handleRemoveSelf}
                    onCancel={() => setShowRemoveSelfConfirm(false)}
                    onClose={() => setShowRemoveSelfConfirm(false)}
                    isDestructive={true}
                />
            </div>
        );
    }

    const totalAdmins = 1 + admins.length;

    return (
        <div className="px-2">
            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group text-left"
                >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center shrink-0">
                            <UserPlus size={20} />
                        </div>
                        <div className="min-w-0 pr-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 line-clamp-2 leading-tight">
                                {(t.cars as any).adminInfoText}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                            ( {totalAdmins} )
                        </span>
                        {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                    </div>
                </button>

                {isExpanded && (
                    <div className="px-5 pb-5 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="pt-4 border-t border-gray-50 dark:border-white/5">
                            {/* Pro Restriction Alert */}
                            {!isPro && !isAdminView && (
                                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-2xl flex items-start gap-4">
                                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center shrink-0">
                                        <Rocket size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-red-800 dark:text-red-300 mb-1 leading-tight">
                                            {(t.cars as any).proFeatureAdminTitle || 'Pro Feature'}
                                        </p>
                                        <p className="text-xs font-medium text-red-600 dark:text-red-400/80 mb-2 leading-relaxed">
                                            {(t.cars as any).proFeatureAdminDesc || 'Upgrade to unlock this feature.'}
                                        </p>
                                        <Link 
                                            to="/settings/plans" 
                                            className="inline-flex items-center text-xs font-black text-red-700 dark:text-red-300 hover:underline"
                                        >
                                            {t.settings.upgradePlan || 'Upgrade Plan'} →
                                        </Link>
                                    </div>
                                </div>
                            )}

                            {/* Add admin row */}
                            <div className="flex gap-2 mb-4">
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => { setNewEmail(e.target.value); setError(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAdmin(); } }}
                                    placeholder={(t.cars as any).addAdminPlaceholder}
                                    disabled={!isPro && !isAdminView}
                                    className="flex-1 px-4 py-2 text-sm bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-gray-900 dark:text-white disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddAdmin}
                                    disabled={adding || !newEmail || (!isPro && !isAdminView)}
                                    className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 dark:hover:bg-blue-400 transition-colors disabled:opacity-50 flex items-center justify-center shrink-0"
                                >
                                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                </button>
                            </div>
                            {error && <p className="text-xs font-medium text-red-500 mb-4 ml-1">{error}</p>}

                            <ul className="space-y-2">
                                {/* Owner is always implicitly there in the count */}
                                <li className="px-4 py-3 bg-gray-50/50 dark:bg-slate-800/30 rounded-xl flex items-center justify-between opacity-70 border border-gray-100 dark:border-white/5">
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                        {(t.cars as any).ownerEmail} ({(t.cars as any).defaultAllowed})
                                    </span>
                                </li>

                                {admins.map((admin) => (
                                    <li key={admin.email} className="px-4 py-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-white/5 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white break-all">{admin.email}</span>
                                        <button
                                            type="button"
                                            onClick={() => confirmRemoveAdmin(admin.email)}
                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-2 shrink-0"
                                            aria-label={(t.cars as any).removeAdmin}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </li>
                                ))}

                                {admins.length === 0 && (
                                    <li className="py-2 text-center text-xs text-gray-500 italic">
                                        {(t.cars as any).noAdminsYet}
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={showRemoveConfirm}
                title={(t.cars as any).removeAdmin}
                message={(t.cars as any).removeAdminConfirm}
                confirmLabel={t.common.delete || 'Remove'}
                cancelLabel={t.common.cancel}
                onConfirm={handleRemoveAdmin}
                onCancel={() => setShowRemoveConfirm(false)}
                onClose={() => setShowRemoveConfirm(false)}
                isDestructive={true}
            />
        </div>
    );
}
